<template>
    <div class="age-range">
        <Card size="small">
            <header>
                <div class="name">{{ $t('venue_statistics.age_range.headline') }}</div>
                <Select v-model="select" :options="options"></Select>
            </header>
            <div v-if="showEmptyState" class="empty-state">
                <p>
                    {{ $t('venue_statistics.age_range.empty_state') }}
                </p>
            </div>
            <div v-else class="chart-content">
                <div v-for="(el, index) in chartData" :key="index" class="bar">
                    <div class="data" :style="{ height: `${chartDataPercentage[index]}%` }" :class="{ zero: el === 0 }">
                        <DTooltip>{{ occurrenceFormatted(el) }} {{ $t('general.visitors') }}</DTooltip>
                    </div>
                </div>
                <div v-for="index2 in 5" :key="index2 + 1000" class="indicator">
                    <div class="inner-line"></div>
                </div>
                <div v-for="label in labels" :key="label" class="label">{{ label }}</div>
            </div>
        </Card>
    </div>
</template>

<script>
import Card from '@/components/Card'
import Select from '@/components/Select'
import DTooltip from '@/components/statistics/DTooltip'

export default {
    name: 'AgeRange',
    components: {
        Card,
        Select,
        DTooltip,
    },
    props: {
        venueAnalytics: {
            type: Object,
            default: undefined,
        },
        historicalData: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            options: [
                { value: 'percent', text: '%' },
                { value: 'sum', text: 'Sum' },
            ],
            select: 'sum',
            chart: null,
            labels: ['<18', '18-21', '22-28', '29-35', '35+'],
        }
    },
    computed: {
        showEmptyState() {
            if (this.historicalData) return false

            const a = !(
                this.venueAnalytics &&
                this.venueAnalytics.visitors &&
                this.venueAnalytics.visitors.ageDistribution
            )

            if (!a) {
                let c = 0
                for (const b of this.venueAnalytics.visitors.ageDistribution) {
                    c += b.occurrence
                }
                return c === 0
            }

            return a
        },
        chartData() {
            const list = [0, 0, 0, 0, 0]

            if (!(this.venueAnalytics && this.venueAnalytics.visitors)) {
                return list
            }

            for (const d of this.venueAnalytics.visitors.ageDistribution) {
                if (d.age < 18) {
                    list[0] += d.occurrence
                }
                if (17 < d.age && d.age < 22) {
                    list[1] += d.occurrence
                }
                if (22 <= d.age && d.age < 29) {
                    list[2] += d.occurrence
                }
                if (29 <= d.age && d.age < 35) {
                    list[3] += d.occurrence
                }
                if (35 <= d.age) {
                    list[4] += d.occurrence
                }
            }

            return list
        },
        chartDataPercentage() {
            if (!this.venueAnalytics) return 0

            const data = this.chartData

            const max = Math.max(...data)
            return data.map((el) => (el / max) * 100)
        },
    },
    methods: {
        occurrenceFormatted(occurrence) {
            if (this.select === 'percent') {
                const cV = this.venueAnalytics.visitors
                const total = cV.ageDistribution.reduce((sum, age) => {
                    return sum + age.occurrence
                }, 0)
                const percentage = Math.round((occurrence / total) * 100)
                return `${percentage || 0} %`
            }

            return occurrence
        },
    },
}
</script>

<style scoped lang="sass">
.age-range
    max-width: 100vw
    width: 100%
    box-sizing: border-box

    .empty-state
        display: flex
        justify-content: center
        align-items: center
        height: 100px
        text-align: center
        user-select: none

    .card
        min-height: 229px
        box-sizing: border-box
        background: #ffffff !important
    header
        display: flex
        justify-content: space-between
        margin-bottom: $l

        .d-select
            min-width: 80px

        .name
            color: $neutral-black
            font-weight: 600
            padding: $xs
            font-size: 14px
            line-height: 17px

    .chart-content
        margin: 0 auto
        width: 244px
        display: grid
        grid-row-gap: 12px
        grid-template-areas: "data data data data data" "indicator indicator indicator indicator indicator" "label label label label label"

        .bar
            height: 90px
            display: flex
            justify-content: center
            width: 100%
            align-items: flex-end

            .data
                width: 20px
                border-radius: 7px
                position: relative
                display: flex
                justify-content: center
                align-items: flex-end
                background-color: #0399DA

                &.zero
                    min-height: 4px
                    background-color: $gray-1

                &:hover
                    .d-tooltip
                        opacity: 1
                        transform: translateY(0)

        .indicator
            border-top: 1px solid $gray-1
            display: flex
            justify-content: center

            .inner-line
                width: 1px
                height: 8px
                background-color: $gray-1
        .label
            display: flex
            font-size: 12px
            justify-content: center
            color: #575962

        .chart-container
            width: 432px
</style>
